/**
 * Normalize CasaMapa municipality labels (accent + common spelling variants).
 */

export const CASAMAPA_METRO_MUNICIPALITIES = Object.freeze([
  "Medellín",
  "Envigado",
  "Sabaneta",
  "Itagüí",
  "Bello",
  "La Estrella",
]);

function stripAccents(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function municipalityKey(value) {
  return stripAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

const CANONICAL_BY_KEY = Object.freeze(
  Object.fromEntries(
    CASAMAPA_METRO_MUNICIPALITIES.map((name) => [municipalityKey(name), name])
  )
);

/** Extra aliases seen in source data / facets. */
const ALIAS_TO_CANONICAL = Object.freeze({
  medellin: "Medellín",
  envigado: "Envigado",
  sabaneta: "Sabaneta",
  itagui: "Itagüí",
  itagüi: "Itagüí",
  bello: "Bello",
  laestrella: "La Estrella",
});

/**
 * Map a raw municipality string to the canonical CasaMapa label.
 */
export function normalizeCasaMapaMunicipality(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return null;
  }
  const key = municipalityKey(raw);
  if (!key) {
    return null;
  }
  return CANONICAL_BY_KEY[key] || ALIAS_TO_CANONICAL[key] || titleCaseMunicipality(raw);
}

function titleCaseMunicipality(value) {
  return String(value)
    .trim()
    .split(/\s+/)
    .map((part) => {
      if (!part) return part;
      const lower = part.toLocaleLowerCase("es-CO");
      return lower.charAt(0).toLocaleUpperCase("es-CO") + lower.slice(1);
    })
    .join(" ");
}

/**
 * Merge facet rows that are spelling variants of the same municipality.
 * Counts are summed; canonical metro names are preferred for labels/values.
 */
export function mergeCasaMapaMunicipalityFacetOptions(facetRows = []) {
  const rows = Array.isArray(facetRows) ? facetRows : [];
  const byKey = new Map();

  rows.forEach((row) => {
    const raw = row?.value ?? row?.label;
    const canonical = normalizeCasaMapaMunicipality(raw);
    if (!canonical) {
      return;
    }
    const key = municipalityKey(canonical);
    const existing = byKey.get(key);
    const count = Number(row?.count);
    if (existing) {
      existing.count =
        (existing.count != null ? existing.count : 0) +
        (Number.isFinite(count) ? count : 0);
      return;
    }
    byKey.set(key, {
      value: canonical,
      label: canonical,
      count: Number.isFinite(count) ? count : null,
    });
  });

  const preferredOrder = new Map(
    CASAMAPA_METRO_MUNICIPALITIES.map((name, index) => [municipalityKey(name), index])
  );

  return [...byKey.values()].sort((a, b) => {
    const aRank = preferredOrder.has(municipalityKey(a.value))
      ? preferredOrder.get(municipalityKey(a.value))
      : 1000;
    const bRank = preferredOrder.has(municipalityKey(b.value))
      ? preferredOrder.get(municipalityKey(b.value))
      : 1000;
    if (aRank !== bRank) {
      return aRank - bRank;
    }
    return String(a.label).localeCompare(String(b.label), "es");
  });
}

export function normalizeCasaMapaMunicipalityList(values = []) {
  const list = Array.isArray(values)
    ? values
    : String(values || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
  const seen = new Set();
  const normalized = [];
  list.forEach((item) => {
    const next = normalizeCasaMapaMunicipality(item);
    if (!next) {
      return;
    }
    const key = municipalityKey(next);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(next);
  });
  return normalized;
}
